/**
 * Module Contrats — vue détail d'un contrat.
 *
 * Trois panneaux :
 *   1. Informations (fournisseur, abonnement, fréquence, espace client) ;
 *   2. Items du contrat (lignes tarifaires : prix kWh, options…) ;
 *   3. Documents & factures — upload, puis « Interpréter » : l'IA extrait
 *      période, échéances, consommation, montants, anomalies et conseils
 *      (débité en crédits, comme les autres fonctions IA de Life).
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded'

import { contratsApi } from '../api/contratsApi'
import {
  CONTRACTS_ACCENT,
  cardSx,
  contractTypeMeta,
  darkFieldSx,
  DOCUMENT_TYPES,
  documentTypeLabel,
  fmtDate,
  fmtEuro,
  PAYMENT_FREQUENCIES,
} from './shared'

const frequencyLabel = (value) =>
  (PAYMENT_FREQUENCIES.find((f) => f.value === value) || {}).label || value

const STATUS_CHIP = {
  pending: { label: 'À analyser', color: 'rgba(255,255,255,0.5)' },
  processing: { label: 'Analyse…', color: '#5aa7ff' },
  done: { label: 'Analysée', color: '#3ddc97' },
  failed: { label: 'Échec', color: '#ff7a7a' },
}

export default function ContractDetail({ contract, onEdit, onChanged, onDeleted }) {
  const meta = contractTypeMeta(contract.contract_type)
  const [documents, setDocuments] = useState([])
  const [items, setItems] = useState(contract.items || [])
  const [newItem, setNewItem] = useState({ label: '', unit_price: '', unit: '' })
  const [upload, setUpload] = useState({ title: '', document_type: 'invoice', file: null })
  const [uploading, setUploading] = useState(false)
  const [analyzingId, setAnalyzingId] = useState(null)
  const [message, setMessage] = useState(null)
  const [openAnalysis, setOpenAnalysis] = useState(null)

  const loadDocuments = async () => {
    const list = await contratsApi.documents.list(`?contract=${contract.id}`)
    setDocuments(list)
  }

  useEffect(() => {
    loadDocuments()
    setItems(contract.items || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.id])

  // ------------------------------------------------------------------
  // Items du contrat
  // ------------------------------------------------------------------
  const addItem = async () => {
    if (!newItem.label.trim()) return
    const created = await contratsApi.items.create({
      contract: contract.id,
      label: newItem.label.trim(),
      unit_price: newItem.unit_price === '' ? null : newItem.unit_price,
      unit: newItem.unit,
      order: items.length,
    })
    if (created) {
      setItems((prev) => [...prev, created])
      setNewItem({ label: '', unit_price: '', unit: '' })
      onChanged()
    }
  }

  const removeItem = async (id) => {
    if (await contratsApi.items.remove(id)) {
      setItems((prev) => prev.filter((i) => i.id !== id))
      onChanged()
    }
  }

  // ------------------------------------------------------------------
  // Documents & analyse IA
  // ------------------------------------------------------------------
  const handleUpload = async () => {
    if (!upload.file || !upload.title.trim()) {
      setMessage({ type: 'error', text: 'Titre et fichier sont obligatoires.' })
      return
    }
    setUploading(true)
    const created = await contratsApi.uploadDocument({
      contract: contract.id,
      title: upload.title.trim(),
      document_type: upload.document_type,
      file: upload.file,
    })
    setUploading(false)
    if (created) {
      setUpload({ title: '', document_type: 'invoice', file: null })
      setMessage(null)
      await loadDocuments()
      onChanged()
    } else {
      setMessage({ type: 'error', text: "Échec de l'upload du document." })
    }
  }

  const handleAnalyze = async (doc) => {
    setAnalyzingId(doc.id)
    setMessage(null)
    const response = await contratsApi.analyzeDocument(doc.id)
    setAnalyzingId(null)
    if (response && response.status >= 200 && response.status < 300) {
      setMessage({ type: 'success', text: 'Facture interprétée avec succès.' })
      setOpenAnalysis(doc.id)
      await loadDocuments()
      onChanged()
    } else if (response && response.status === 402) {
      setMessage({
        type: 'error',
        text: 'Crédits IA insuffisants pour lancer cette analyse.',
      })
    } else if (response && response.status === 422) {
      setMessage({
        type: 'error',
        text: 'Texte illisible dans ce fichier (PDF scanné sans OCR ?). Essayez un PDF natif.',
      })
      await loadDocuments()
    } else {
      setMessage({ type: 'error', text: "L'analyse a échoué. Vous n'avez pas été débité." })
      await loadDocuments()
    }
  }

  const handleDeleteDocument = async (doc) => {
    if (await contratsApi.documents.remove(doc.id)) {
      await loadDocuments()
      onChanged()
    }
  }

  const handleDeleteContract = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Supprimer le contrat « ${contract.name} » et ses documents ?`)) {
      if (await contratsApi.contracts.remove(contract.id)) onDeleted()
    }
  }

  return (
    <Grid container spacing={2}>
      {/* ------------------------------------------------ Informations */}
      <Grid item xs={12} md={5}>
        <Box sx={cardSx}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              size="small"
              label={meta.label}
              sx={{ bgcolor: `${meta.color}22`, color: meta.color, fontWeight: 700 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={onEdit} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleDeleteContract} sx={{ color: '#ff7a7a' }}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 20, mt: 1 }}>
            {contract.name}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {contract.provider || 'Fournisseur non renseigné'}
          </Typography>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 1.5 }} />
          <InfoRow label="Abonnement" value={`${fmtEuro(contract.subscription_price)} / ${frequencyLabel(contract.payment_frequency).toLowerCase()}`} />
          <InfoRow label="Coût mensuel normalisé" value={fmtEuro(contract.monthly_cost)} />
          <InfoRow label="Référence client" value={contract.customer_reference || '—'} />
          <InfoRow label="Début" value={fmtDate(contract.start_date)} />
          <InfoRow label="Fin" value={fmtDate(contract.end_date)} />
          {contract.notes && <InfoRow label="Notes" value={contract.notes} />}
          {contract.customer_portal_url && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<OpenInNewRoundedIcon />}
              href={contract.customer_portal_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 2, color: CONTRACTS_ACCENT, borderColor: CONTRACTS_ACCENT }}
            >
              Ouvrir l'espace client
            </Button>
          )}
        </Box>

        {/* ------------------------------------------------ Items */}
        <Box sx={{ ...cardSx, mt: 2 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            Items du contrat
          </Typography>
          {items.length === 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>
              Ex. « Prix kWh heures pleines — 0,27 €/kWh »
            </Typography>
          )}
          {items.map((item) => (
            <Stack key={item.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
              <Typography sx={{ color: '#fff', fontSize: 14, flexGrow: 1 }}>
                {item.label}
              </Typography>
              {item.unit_price !== null && item.unit_price !== undefined && item.unit_price !== '' && (
                <Typography sx={{ color: meta.color, fontSize: 14, fontWeight: 700 }}>
                  {Number(item.unit_price).toLocaleString('fr-FR', { maximumFractionDigits: 4 })}
                  {item.unit ? ` ${item.unit}` : ' €'}
                </Typography>
              )}
              <IconButton size="small" onClick={() => removeItem(item.id)} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <TextField
              size="small"
              placeholder="Libellé"
              value={newItem.label}
              onChange={(e) => setNewItem((s) => ({ ...s, label: e.target.value }))}
              sx={{ ...darkFieldSx, flexGrow: 1 }}
            />
            <TextField
              size="small"
              placeholder="Prix"
              type="number"
              value={newItem.unit_price}
              onChange={(e) => setNewItem((s) => ({ ...s, unit_price: e.target.value }))}
              sx={{ ...darkFieldSx, width: 90 }}
            />
            <TextField
              size="small"
              placeholder="€/kWh"
              value={newItem.unit}
              onChange={(e) => setNewItem((s) => ({ ...s, unit: e.target.value }))}
              sx={{ ...darkFieldSx, width: 90 }}
            />
            <IconButton onClick={addItem} sx={{ color: CONTRACTS_ACCENT }}>
              <AddRoundedIcon />
            </IconButton>
          </Stack>
        </Box>
      </Grid>

      {/* ------------------------------------------------ Documents & factures */}
      <Grid item xs={12} md={7}>
        <Box sx={cardSx}>
          <Typography sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}>
            Documents & factures
          </Typography>

          {/* Upload */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Titre (ex. Facture mars 2026)"
              value={upload.title}
              onChange={(e) => setUpload((s) => ({ ...s, title: e.target.value }))}
              sx={{ ...darkFieldSx, flexGrow: 1 }}
            />
            <TextField
              size="small"
              select
              value={upload.document_type}
              onChange={(e) => setUpload((s) => ({ ...s, document_type: e.target.value }))}
              sx={{ ...darkFieldSx, minWidth: 140 }}
            >
              {DOCUMENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              sx={{
                color: upload.file ? '#3ddc97' : 'rgba(255,255,255,0.7)',
                borderColor: 'rgba(255,255,255,0.25)',
                whiteSpace: 'nowrap',
              }}
            >
              {upload.file ? upload.file.name.slice(0, 18) : 'Fichier'}
              <input
                hidden
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setUpload((s) => ({ ...s, file: e.target.files[0] || null }))}
              />
            </Button>
            <Button
              variant="contained"
              disabled={uploading}
              onClick={handleUpload}
              sx={{ bgcolor: CONTRACTS_ACCENT, color: '#00001d', fontWeight: 700, '&:hover': { bgcolor: '#ffc76e' } }}
            >
              {uploading ? '…' : 'Ajouter'}
            </Button>
          </Stack>

          {message && (
            <Typography
              sx={{
                color: message.type === 'error' ? '#ff7a7a' : '#3ddc97',
                fontSize: 14,
                mb: 1.5,
              }}
            >
              {message.text}
            </Typography>
          )}

          {documents.length === 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>
              Attachez le contrat signé, les CGV et surtout vos factures : l'IA en
              extraira consommation, coûts et anomalies.
            </Typography>
          )}

          {documents.map((doc) => {
            const chip = STATUS_CHIP[doc.analysis_status] || STATUS_CHIP.pending
            const isInvoice = doc.document_type === 'invoice'
            const expanded = openAnalysis === doc.id && doc.analysis
            return (
              <Box
                key={doc.id}
                sx={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 15 }} noWrap>
                      {doc.file_url ? (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#fff', textDecoration: 'none' }}
                        >
                          {doc.title}
                        </a>
                      ) : (
                        doc.title
                      )}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                      {documentTypeLabel(doc.document_type)} — {fmtDate(doc.document_date || doc.uploaded_at)}
                    </Typography>
                  </Box>
                  {isInvoice && (
                    <Chip size="small" label={chip.label} sx={{ color: chip.color, bgcolor: `${chip.color}22` }} />
                  )}
                  {isInvoice && (
                    <Button
                      size="small"
                      disabled={analyzingId === doc.id}
                      startIcon={
                        analyzingId === doc.id ? (
                          <CircularProgress size={14} sx={{ color: CONTRACTS_ACCENT }} />
                        ) : (
                          <AutoAwesomeRoundedIcon />
                        )
                      }
                      onClick={() =>
                        doc.analysis && analyzingId !== doc.id && openAnalysis !== doc.id
                          ? setOpenAnalysis(doc.id)
                          : handleAnalyze(doc)
                      }
                      sx={{ color: CONTRACTS_ACCENT, whiteSpace: 'nowrap' }}
                    >
                      {analyzingId === doc.id
                        ? 'Analyse…'
                        : doc.analysis
                        ? openAnalysis === doc.id
                          ? 'Ré-analyser'
                          : 'Voir l’analyse'
                        : 'Interpréter'}
                    </Button>
                  )}
                  <IconButton size="small" onClick={() => handleDeleteDocument(doc)} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>

                {expanded && <AnalysisPanel analysis={doc.analysis} accent={meta.color} />}
              </Box>
            )
          })}
        </Box>
      </Grid>
    </Grid>
  )
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>{label}</Typography>
      <Typography sx={{ color: '#fff', fontSize: 14, textAlign: 'right', ml: 2 }}>
        {value}
      </Typography>
    </Stack>
  )
}

/** Restitution de l'interprétation IA d'une facture. */
function AnalysisPanel({ analysis, accent }) {
  if (!analysis) return null
  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(255,255,255,0.12)' }}>
      {analysis.summary && (
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, mb: 1.5 }}>
          {analysis.summary}
        </Typography>
      )}
      <Grid container spacing={1}>
        <MetricBox label="Période" value={`${fmtDate(analysis.period_start)} → ${fmtDate(analysis.period_end)}`} />
        <MetricBox
          label="Consommation"
          value={
            analysis.consumption_value
              ? `${Number(analysis.consumption_value).toLocaleString('fr-FR')} ${analysis.consumption_unit || ''}`
              : '—'
          }
          accent={accent}
        />
        <MetricBox label="Total TTC" value={fmtEuro(analysis.total_amount)} accent={accent} />
        <MetricBox label="Abonnement" value={fmtEuro(analysis.subscription_amount)} />
        <MetricBox label="Consommation (€)" value={fmtEuro(analysis.consumption_amount)} />
        <MetricBox label="Taxes" value={fmtEuro(analysis.taxes_amount)} />
        <MetricBox
          label="Prix unitaire"
          value={
            analysis.unit_price
              ? `${Number(analysis.unit_price).toLocaleString('fr-FR', { maximumFractionDigits: 4 })} €`
              : '—'
          }
        />
        <MetricBox label="Échéance" value={fmtDate(analysis.due_date)} />
      </Grid>
      {(analysis.anomalies || []).length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {analysis.anomalies.map((a, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 16, color: '#ffb547', mt: '2px' }} />
              <Typography sx={{ color: '#ffcf8a', fontSize: 13 }}>{a}</Typography>
            </Stack>
          ))}
        </Box>
      )}
      {(analysis.advice || []).length > 0 && (
        <Box sx={{ mt: 1 }}>
          {analysis.advice.map((a, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
              <TipsAndUpdatesRoundedIcon sx={{ fontSize: 16, color: '#3ddc97', mt: '2px' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>{a}</Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  )
}

function MetricBox({ label, value, accent }) {
  return (
    <Grid item xs={6} sm={3}>
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 1.5, p: 1 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{label}</Typography>
        <Typography sx={{ color: accent || '#fff', fontSize: 14, fontWeight: 700 }}>
          {value}
        </Typography>
      </Box>
    </Grid>
  )
}
